import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getRecuperacionAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import {
  formatearFecha,
  formatearFechaHora,
  formatearMonto,
} from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";

// Una fila por abono (recibo): sus montos se suman directo.
const dinero = (key, label) => ({
  key,
  label,
  align: "center",
  total: true,
  formatoTotal: formatearMonto,
  valor: (a) => formatearMonto(a[key]),
  excel: (a) => a[key],
});

const COLUMNAS = [
  {
    key: "id",
    label: "Transacción",
    align: "center",
    valor: (a) => a.id,
  },
  {
    key: "fullName",
    label: "Cliente",
    align: "left",
    texto: true,
    valor: (a) => a.fullName,
  },
  {
    key: "creditId",
    label: "#Crédito",
    align: "center",
    valor: (a) => a.creditId,
  },
  {
    // Texto: así el orden inicial es el cronológico (de más antiguo a más reciente),
    // el mismo con que el servidor entrega los abonos; las fechas ISO se comparan bien.
    key: "fechaPago",
    label: "F/H Pago",
    align: "center",
    texto: true,
    valor: (a) => formatearFechaHora(a.fechaPago),
    excel: (a) => formatearFechaHora(a.fechaPago),
  },
  dinero("aCapital", "A Capital"),
  dinero("aIntCorriente", "A.Int.Corriente"),
  dinero("totalRecibido", "Total Recibido"),
];

const Recuperacion = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { fechaDesde, fechaHasta, selectedGestor } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getRecuperacionAsync(token, {
          desde: fechaDesde.toString(),
          hasta: fechaHasta.toString(),
          gestorId: selectedGestor === TODOS ? 0 : selectedGestor,
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [token, selectedGestor, fechaDesde, fechaHasta, setIsLoading, manejarError]);

  // Quien cobró el abono (RealizadoPor), con el nombre que manda el servidor: también
  // el de un gestor dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Recuperación"
      nombreArchivo="Recuperacion"
      parametros={[
        { label: "Gestor Seleccionado", valor: gestorName },
        { label: "Desde", valor: formatearFecha(fechaDesde) },
        { label: "Hasta", valor: formatearFecha(fechaHasta) },
      ]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      unidad="abono"
      unidadPlural="abonos"
      ordenInicial="fechaPago"
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default Recuperacion;
